import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Container,
  Paper,
  Breadcrumbs,
  Link,
  InputBase
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { usePrices } from '../hooks/usePrices';
import TokenIcon from '../components/TokenIcon';
import CoinChart from '../components/CoinChart';
import api from '../services/api';
import { formatBalance, formatCurrency, formatTriggerPrice } from '../utils/format';
import TakeProfitStrategy from '../components/TakeProfitStrategy';
import StopLossStrategy from '../components/StopLossStrategy';
import { useTranslation } from 'react-i18next';

const ExitStrategyPage = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { coinSymbol } = useParams();
  const [searchParams] = useSearchParams();
  const strategyTypeFromUrl = searchParams.get('type');
  const [strategies, setStrategies] = useState([]);
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [percentValue, setPercentValue] = useState('');
  const [rawPercentValue, setRawPercentValue] = useState('');
  const [historicalHighs, setHistoricalHighs] = useState({ '1M': null, '3M': null, '6M': null, '1Y': null });
  const [historicalLows, setHistoricalLows] = useState({ '1M': null, '3M': null, '6M': null, '1Y': null });
  const [selectedPreset, setSelectedPreset] = useState(strategyTypeFromUrl === 'stop_loss' ? '-25%' : '+25%');
  const [loadingHistorical, setLoadingHistorical] = useState(false);
  const [formData, setFormData] = useState({
    strategy_type: strategyTypeFromUrl || 'take_profit',
    target_price: '',
    steps: [
      { trigger_price: '', percentage_to_sell: '50' },
      { trigger_price: '', percentage_to_sell: '50' }
    ]
  });
  const [customValue, setCustomValue] = useState('');
  const [rawHoldings, setRawHoldings] = useState(null);
  const [holdings, setHoldings] = useState(null);
  const [loadedStrategy, setLoadedStrategy] = useState(null);
  // Track if defaults have been set to avoid repeated updates
  const [defaultsSet, setDefaultsSet] = useState(false);

  // Add a log at the top of the component to show target_price on every render
  // console.log('[RENDER] formData.target_price:', formData.target_price);

  // Memoize the symbols array to prevent usePrices from re-running
  const symbols = useMemo(() => [coinSymbol], [coinSymbol]);

  // Fetch current price for this coin using the same service as CoinPage
  const { prices, loading: pricesLoading } = usePrices(symbols, false, 'ExitStrategyPage');
  const currentPrice = prices?.[coinSymbol]?.usd || 0;

  // Fetch holdings for this coin (like CoinPage)
  useEffect(() => {
    if (!coinSymbol) return;
    const fetchHoldings = async () => {
      try {
        const upperSymbol = coinSymbol.toUpperCase();
        const holdingsResponse = await api.get(`/api/wallets/balances?coin=${coinSymbol}`);
        const coinHoldings = holdingsResponse.data.find(h => h.coin === upperSymbol);
        if (coinHoldings) {
          setRawHoldings(coinHoldings);
        } else {
          setRawHoldings(null);
          setHoldings(null);
        }
      } catch (err) {
        setRawHoldings(null);
        setHoldings(null);
      }
    };
    fetchHoldings();
  }, [coinSymbol]);

  // Calculate holdings values using useMemo
  const calculatedHoldings = useMemo(() => {
    if (rawHoldings && currentPrice > 0) {
      const balance = Number(rawHoldings.balance || 0);
      const avgBuyPrice = Number(rawHoldings.avg_buy_price || 0);
      const totalValue = balance * currentPrice;
      const totalCost = balance * avgBuyPrice;
      const profitLoss = totalValue - totalCost;
      const profitLossPercentage = avgBuyPrice > 0 ? ((currentPrice - avgBuyPrice) / avgBuyPrice) * 100 : 0;
      return {
        balance,
        avgBuyPrice,
        totalValue: Number(totalValue),
        totalCost: Number(totalCost),
        profitLoss: Number(profitLoss),
        profitLossPercentage: Number(profitLossPercentage)
      };
    }
    return null;
  }, [currentPrice, rawHoldings?.balance, rawHoldings?.avg_buy_price]);

  useEffect(() => {
    setHoldings(calculatedHoldings);
  }, [calculatedHoldings]);

  // Fix price parsing to handle comma-formatted numbers
  const priceNum = Number(formData.target_price) || 0;
  const showTargetPriceError =
    priceNum &&
    currentPrice &&
    !pricesLoading &&
    currentPrice > 0 &&
    ((formData.strategy_type === 'take_profit' && priceNum < currentPrice) ||
     (formData.strategy_type === 'stop_loss' && priceNum >= currentPrice));

  // console.log('DEBUG target price error:', {
  //   formDataTargetPrice: formData.target_price,
  //   priceNum,
  //   currentPrice,
  //   pricesLoading,
  //   showTargetPriceError
  // });

  useEffect(() => {
    if (coinSymbol) {
      fetchStrategies();
      fetchSuggestions();
      // Auto-fetch all historical data based on strategy type
      if (formData.strategy_type === 'take_profit') {
        // Fetch all historical highs
        fetchHistoricalHigh('1M HIGH');
        fetchHistoricalHigh('3M HIGH');
        fetchHistoricalHigh('6M HIGH');
        fetchHistoricalHigh('1Y HIGH');
      } else {
        // Fetch all historical lows
        fetchHistoricalLow('1M LOW');
        fetchHistoricalLow('3M LOW');
        fetchHistoricalLow('6M LOW');
        fetchHistoricalLow('1Y LOW');
      }
    }
  }, [coinSymbol]);

  // Auto-calculate trigger prices when target price or number of steps changes
  useEffect(() => {
    if (formData.target_price && currentPrice > 0) {
      const hasActiveSteps = formData.steps.some(step => Number(step.percentage_to_sell) > 0);
      if (hasActiveSteps) {
        calculateTriggerPrices(formData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.target_price, currentPrice, formData.steps.length]);

  // Ensure calculation runs on mount if target price and steps exist
  useEffect(() => {
    if (formData.target_price && currentPrice > 0 && formData.steps.length > 0) {
      // Only calculate if there are steps with percentage_to_sell > 0
      const hasActiveSteps = formData.steps.some(step => Number(step.percentage_to_sell) > 0);
      if (hasActiveSteps) {
        calculateTriggerPrices(formData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loading && !strategies.length && currentPrice && !defaultsSet) {
      const newTargetPrice = formData.strategy_type === 'take_profit' 
        ? (currentPrice * 1.25).toFixed(2) 
        : (currentPrice * 0.75).toFixed(2);
      setFormData({
        strategy_type: formData.strategy_type,
        target_price: newTargetPrice,
        steps: [
          { trigger_price: '', percentage_to_sell: '50' },
          { trigger_price: '', percentage_to_sell: '50' }
        ]
      });
      setSelectedPreset(formData.strategy_type === 'take_profit' ? '+25%' : '-25%');
      setDefaultsSet(true);
    }
  }, [loading, strategies.length, currentPrice, defaultsSet, formData.strategy_type]);

  const fetchStrategies = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/exit-strategies/coin/${coinSymbol}`);
      const fetchedStrategies = response.data.strategies || [];
      setStrategies(fetchedStrategies);
      
      // Auto-load strategy of current type if it exists
      const existing = fetchedStrategies.find(s => s.strategy_type === formData.strategy_type);
      if (existing) {
        handleEditStrategy(existing);
      } else {
        // Reset to blank form for this type, but let the effect above handle the default values
        setDefaultsSet(false);
      }
    } catch (err) {
      console.error('Error fetching strategies:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // When user changes strategy type, load existing or blank
  useEffect(() => {
    if (strategies && strategies.length > 0) {
      const existing = strategies.find(s => s.strategy_type === formData.strategy_type);
      if (existing) {
        handleEditStrategy(existing);
      } else {
        setFormData({
          strategy_type: formData.strategy_type,
          target_price: '',
          steps: [
            { trigger_price: '', percentage_to_sell: '50' },
            { trigger_price: '', percentage_to_sell: '50' }
          ]
        });
        // Set default preset for new strategy type
        setSelectedPreset(formData.strategy_type === 'take_profit' ? '+25%' : '-25%');
        
        // Automatically fetch 6M high/low for the new strategy type
        if (formData.strategy_type === 'take_profit') {
          fetchHistoricalHigh('6M HIGH');
        } else if (formData.strategy_type === 'stop_loss') {
          fetchHistoricalLow('6M LOW');
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.strategy_type]);

  const fetchSuggestions = async () => {
    try {
      const response = await api.get(`/api/exit-strategies/suggestions/${coinSymbol}`);
      setSuggestions(response.data);
    } catch (err) {
      console.error('Error fetching suggestions:', err);
    }
  };

  // Fetch historical high for take profit (optimized: fetch all periods once)
  const fetchHistoricalHigh = async (period) => {
    // Extract the actual period from the preset (e.g., '6M HIGH' -> '6M')
    const actualPeriod = period.replace(' HIGH', '');
    
    try {
      setLoadingHistorical(true);
      setSelectedPreset(period);
      
      // If we already have all highs, just use them
      if (historicalHighs[actualPeriod]) {
        setFormData(prev => ({ ...prev, target_price: historicalHighs[actualPeriod] }));
        setSelectedPreset(period);
        return;
      }
      // Otherwise, fetch from API (returns all periods)
      const response = await api.get(`/api/historical-high/${coinSymbol}?period=${actualPeriod}`);
      const { high_1m, high_3m, high_6m, high_1y } = response.data;
      const highs = {
        '1M': high_1m,
        '3M': high_3m,
        '6M': high_6m,
        '1Y': high_1y
      };
      setHistoricalHighs(highs);
      setFormData(prev => ({ ...prev, target_price: highs[actualPeriod] }));
      setSelectedPreset(period);
    } catch (err) {
      console.error(`Error fetching ${period} high:`, err);
      // Fallback to estimated high based on current price
      let estimatedHigh;
      switch (actualPeriod) {
        case '1M':
          estimatedHigh = currentPrice * 1.3;
          break;
        case '3M':
          estimatedHigh = currentPrice * 1.5;
          break;
        case '6M':
          estimatedHigh = currentPrice * 1.8;
          break;
        case '1Y':
          estimatedHigh = currentPrice * 2.0;
          break;
        default:
          estimatedHigh = currentPrice * 1.3;
      }
      setFormData(prev => ({ ...prev, target_price: estimatedHigh }));
    } finally {
      setLoadingHistorical(false);
    }
  };

  // Fetch historical low for stop loss (optimized: fetch all periods once)
  const fetchHistoricalLow = async (period) => {
    // Extract the actual period from the preset (e.g., '6M LOW' -> '6M')
    const actualPeriod = period.replace(' LOW', '');
    
    try {
      setLoadingHistorical(true);
      setSelectedPreset(period);
      
      // If we already have all lows, just use them
      if (historicalLows[actualPeriod]) {
        setFormData(prev => ({ ...prev, target_price: historicalLows[actualPeriod] }));
        setSelectedPreset(period);
        return;
      }
      // Otherwise, fetch from API (returns all periods)
      const response = await api.get(`/api/historical-low/${coinSymbol}?period=${actualPeriod}`);
      const { low_1m, low_3m, low_6m, low_1y } = response.data;
      const lows = {
        '1M': low_1m,
        '3M': low_3m,
        '6M': low_6m,
        '1Y': low_1y
      };
      setHistoricalLows(lows);
      setFormData(prev => ({ ...prev, target_price: lows[actualPeriod] }));
      setSelectedPreset(period);
    } catch (err) {
      console.error(`Error fetching ${period} low:`, err);
      // Fallback to estimated low based on current price
      let estimatedLow;
      switch (actualPeriod) {
        case '1M':
          estimatedLow = currentPrice * 0.7;
          break;
        case '3M':
          estimatedLow = currentPrice * 0.5;
          break;
        case '6M':
          estimatedLow = currentPrice * 0.3;
          break;
        case '1Y':
          estimatedLow = currentPrice * 0.2;
          break;
        default:
          estimatedLow = currentPrice * 0.7;
      }
      setFormData(prev => ({ ...prev, target_price: estimatedLow }));
    } finally {
      setLoadingHistorical(false);
    }
  };


  const handleCreateStrategy = () => {
    setFormData({
      strategy_type: 'take_profit',
      target_price: '',
      steps: [
        { trigger_price: '', percentage_to_sell: '50' },
        { trigger_price: '', percentage_to_sell: '50' }
      ]
    });
  };

  const handleEditStrategy = (strategy) => {
    let steps = [];
    if (typeof strategy.steps === 'string') {
      steps = JSON.parse(strategy.steps || '[]');
    } else if (Array.isArray(strategy.steps)) {
      steps = strategy.steps;
    } else {
      steps = [];
    }
    setFormData({
      strategy_type: strategy.strategy_type,
      target_price: strategy.target_price,
      steps: steps.map(step => ({ ...step })) // Preserve all fields
    });
    setLoadedStrategy({
      id: strategy.id, // Include the strategy ID
      strategy_type: strategy.strategy_type,
      target_price: strategy.target_price,
      steps: steps.map(step => ({ ...step })) // Preserve all fields
    });
  };

  const handleDeleteStrategy = async (strategyId) => {
    if (!window.confirm('Are you sure you want to delete this exit strategy?')) {
      return;
    }

    try {
      await api.delete(`/api/exit-strategies/${strategyId}`);
      fetchStrategies();
    } catch (err) {
      console.error('Error deleting strategy:', err);
      setError(err.message);
    }
  };

  const handleSubmit = async () => {
    try {
      // Validate form data
      if (!formData.target_price || formData.steps.length === 0) {
        setError('Please fill in all required fields');
        return;
      }

      // Validate each step
      for (let i = 0; i < formData.steps.length; i++) {
        const step = formData.steps[i];
        
        // Check trigger price
        if (!step.trigger_price || isNaN(step.trigger_price) || Number(step.trigger_price) <= 0) {
          setError(`Step ${i + 1}: Invalid trigger price. Must be greater than 0.`);
          return;
        }
        
        // Check percentage
        if (!step.percentage_to_sell || isNaN(step.percentage_to_sell) || Number(step.percentage_to_sell) <= 0) {
          setError(`Step ${i + 1}: Invalid percentage. Must be greater than 0.`);
          return;
        }
        
        if (Number(step.percentage_to_sell) > 100) {
          setError(`Step ${i + 1}: Percentage cannot exceed 100%.`);
          return;
        }
      }

      const totalPercentage = formData.steps.reduce((sum, step) => sum + Number(step.percentage_to_sell || 0), 0);
      if (totalPercentage > 100) {
        setError('Total percentage cannot exceed 100%');
        return;
      }

      // Calculate amount for each step
      const balance = holdings?.balance || 0;
      const stepsWithAmount = formData.steps.map(step => ({
        trigger_price: Number(step.trigger_price),
        percentage_to_sell: Number(step.percentage_to_sell),
        amount: Number(balance) * (Number(step.percentage_to_sell) / 100)
      }));

      const strategyData = {
        coin_symbol: coinSymbol,
        strategy_type: formData.strategy_type,
        target_price: Number(formData.target_price),
        steps: stepsWithAmount
      };

      await api.post('/api/exit-strategies', strategyData);
      fetchStrategies();
      setError(null);
      // Reset form
      setFormData({
        strategy_type: 'take_profit',
        target_price: '',
        steps: [
          { trigger_price: '', percentage_to_sell: '50' },
          { trigger_price: '', percentage_to_sell: '50' }
        ]
      });
    } catch (err) {
      console.error('Error creating strategy:', err);
      
      // Extract server error message if available
      let errorMessage = 'Failed to create strategy';
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    }
  };

  const addStep = () => {
    if (formData.steps.length < 4) {
      // Calculate the remaining percentage to reach 100%
      const currentTotal = formData.steps.reduce((sum, step) => sum + Number(step.percentage_to_sell || 0), 0);
      const remainingPercentage = Math.max(0, 100 - currentTotal);
      
      const newSteps = [...formData.steps, { trigger_price: '', percentage_to_sell: remainingPercentage.toString() }];
      setFormData({
        ...formData,
        steps: newSteps
      });
      // No need to call calculateTriggerPrices here; useEffect will handle it
    }
  };

  const removeStep = async (index) => {
    // If this is the last step and we have a loaded strategy, delete the entire strategy
    if (formData.steps.length === 1 && loadedStrategy) {
      // Validate that we have a valid strategy ID
      if (!loadedStrategy.id) {
        console.error('Loaded strategy missing ID:', loadedStrategy);
        setError('Cannot delete strategy: Missing strategy ID. Please refresh the page and try again.');
        return;
      }
      
      try {
        if (window.confirm('This will delete the last step and remove the entire exit strategy. Are you sure?')) {
          await api.delete(`/api/exit-strategies/${loadedStrategy.id}`);
          
          // Reset form to default state
          setFormData({
            strategy_type: formData.strategy_type,
            target_price: '',
            steps: [
              { trigger_price: '', percentage_to_sell: '50' },
              { trigger_price: '', percentage_to_sell: '50' }
            ]
          });
          
          // Reset other state
          setSelectedPreset(formData.strategy_type === 'take_profit' ? '+25%' : '-25%');
          setPercentValue('');
          setRawPercentValue('');
          
          // Show success message
          setError(null);
          setInfo('Exit strategy deleted successfully. Form reset to default state.');
          
          // Refresh strategies
          fetchStrategies();
          
          // Clear the loaded strategy
          setLoadedStrategy(null);
        }
      } catch (err) {
        console.error('Error deleting strategy:', err);
        setError('Failed to delete strategy: ' + err.message);
      }
      return;
    }
    
    // Normal step removal
    if (formData.steps.length > 1) {
      const newSteps = formData.steps.filter((_, i) => i !== index);
      setFormData({ ...formData, steps: newSteps });
      // No need to call calculateTriggerPrices here; useEffect will handle it
    }
  };

  // Calculate trigger price from percentage
  const calculateTriggerPriceFromPercentage = (percentage, currentPrice, targetPrice) => {
    if (!currentPrice || !targetPrice || percentage <= 0) return 0;
    
    const priceDifference = targetPrice - currentPrice;
    const percentageDecimal = percentage / 100;
    
    // For take profit: trigger price is between current price and target price
    // For stop loss: trigger price is below current price
    if (formData.strategy_type === 'take_profit') {
      return currentPrice + (priceDifference * percentageDecimal);
    } else {
      // For stop loss, percentage represents how much below current price
      return currentPrice - (currentPrice * percentageDecimal);
    }
  };

  // Calculate percentage from trigger price
  const calculatePercentageFromTriggerPrice = (triggerPrice, currentPrice, targetPrice) => {
    if (!currentPrice || !targetPrice || triggerPrice <= 0) return 0;
    
    if (formData.strategy_type === 'take_profit') {
      const priceDifference = targetPrice - currentPrice;
      if (priceDifference <= 0) return 0;
      
      const triggerDifference = triggerPrice - currentPrice;
      return (triggerDifference / priceDifference) * 100;
    } else {
      // For stop loss, calculate percentage below current price
      const priceDifference = currentPrice - triggerPrice;
      return (priceDifference / currentPrice) * 100;
    }
  };

  const updateStep = (index, field, value) => {
    const newSteps = [...formData.steps];
    newSteps[index][field] = value;

    // Only recalculate the other field if the trigger price is changed
    if (field === 'trigger_price' && formData.target_price && currentPrice > 0) {
      const targetPrice = Number(formData.target_price);
      const triggerPrice = Number(value);
      if (triggerPrice > 0) {
        const percentage = calculatePercentageFromTriggerPrice(triggerPrice, currentPrice, targetPrice);
        newSteps[index].percentage_to_sell = percentage.toFixed(2);
      }
    }
    // Do NOT recalculate trigger price when percentage_to_sell is changed

    setFormData(prev => ({ ...prev, steps: newSteps }));
  };

  // Auto-calculate trigger prices based on target price and current price
  const calculateTriggerPrices = (formDataArg) => {
    const formDataToUse = formDataArg || formData;
    // console.log('[calculateTriggerPrices] Starting with steps:', formDataToUse.steps.length);
    if (!formDataToUse.target_price || !currentPrice || currentPrice <= 0) {
      // console.log('[calculateTriggerPrices] Early return - missing data');
      return;
    }

    const targetPrice = Number(formDataToUse.target_price);
    const numSteps = formDataToUse.steps.length;

    if (formData.strategy_type === 'take_profit') {
      const priceDifference = targetPrice - currentPrice;
      if (priceDifference < 0) {
        // console.log('[calculateTriggerPrices] Early return - invalid price difference');
        return;
      }
      if (priceDifference === 0) {
        // Set all triggers to current price
        const newSteps = formDataToUse.steps.map(step => ({ ...step, trigger_price: currentPrice }));
        setFormData(prev => ({ ...prev, steps: newSteps }));
        return;
      }
      // Distribute trigger prices upward
      const newSteps = formDataToUse.steps.map((step, i) => {
        const trigger = currentPrice + priceDifference * ((i + 1) / numSteps);
        return {
          ...step,
          trigger_price: trigger
        };
      });
      setFormData(prev => ({ ...prev, steps: newSteps }));
      return;
    }

    // STOP LOSS: Distribute trigger prices downward
    const priceDifference = currentPrice - targetPrice;
    if (priceDifference < 0) {
      // console.log('[calculateTriggerPrices] Early return - invalid price difference (stop loss)');
      return;
    }
    if (priceDifference === 0) {
      // Set all triggers to current price
      const newSteps = formDataToUse.steps.map(step => ({ ...step, trigger_price: currentPrice }));
      setFormData(prev => ({ ...prev, steps: newSteps }));
      return;
    }
    const newSteps = formDataToUse.steps.map((step, i) => {
      const trigger = currentPrice - priceDifference * ((i + 1) / numSteps);
      return {
        ...step,
        trigger_price: trigger
      };
    });
    setFormData(prev => ({ ...prev, steps: newSteps }));
  };

  const calculateSuggestedSteps = () => {
    if (!suggestions || !formData.target_price) return;

    const currentPrice = suggestions.current_price;
    const targetPrice = Number(formData.target_price);
    const priceDifference = targetPrice - currentPrice;
    const numSteps = formData.steps.length;

    if (formData.strategy_type === 'take_profit' && priceDifference > 0) {
      const stepSize = priceDifference / numSteps;
      const percentagePerStep = 100 / numSteps;

      const newSteps = [];
      for (let i = 0; i < numSteps; i++) {
        const triggerPrice = currentPrice + (stepSize * (i + 1));
        newSteps.push({
          trigger_price: triggerPrice.toFixed(4),
          percentage_to_sell: percentagePerStep.toFixed(2)
        });
      }
      setFormData({ ...formData, steps: newSteps });
    }
  };



  const getStrategyTypeLabel = (type) => {
    return type === 'take_profit' ? t('exitStrategy.takeProfit') : t('exitStrategy.stopLoss');
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography>{t('common.loading')}</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs sx={{ mb: 1 }}>
          <Link 
            component="button" 
            variant="body2" 
            onClick={() => navigate(-1)}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <ArrowBackIcon fontSize="small" />
            {t('exitStrategy.back')}
          </Link>
        </Breadcrumbs>
        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
          <TokenIcon coinSymbol={coinSymbol} size={32} />
          <Box>
            <Typography variant="h6" sx={{ lineHeight: 1.2, fontWeight: 600 }}>
              {coinSymbol} {t('exitStrategy.exitStrategies')}
            </Typography>
            <Typography 
              variant="body2" 
              color="textSecondary" 
              sx={{ lineHeight: 1.2 }}
              dangerouslySetInnerHTML={{
                __html: t('exitStrategy.currentPrice', { price: formatCurrency(currentPrice) })
              }}
            />
            <Typography 
              variant="caption" 
              color="textSecondary" 
              sx={{ lineHeight: 1.2 }}
              dangerouslySetInnerHTML={{
                __html: t('exitStrategy.balance', { balance: holdings ? `${formatBalance(holdings.balance || 0)} ${coinSymbol}` : '0.00' })
              }}
            />
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {info && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}

      <Grid container spacing={1}>
        {/* Create/Edit Form - Full Width */}
        <Grid sx={{ width: '100%' }}>
          {/* Removed Paper wrapper here */}
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'baseline' }}>
              <span style={{ fontSize: '1.7em', fontWeight: 900, lineHeight: 1 }}>1<span style={{ fontSize: '1em', fontWeight: 900 }}>. </span></span>
              <span style={{ marginLeft: 6 }}>{t('exitStrategy.chooseStrategy')}</span>
            </Typography>
            {/* Strategy Type Selection */}
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('exitStrategy.strategyType')}</InputLabel>
                <Select
                  value={formData.strategy_type}
                  onChange={(e) => {
                    setFormData({ ...formData, strategy_type: e.target.value });
                    setInfo(null); // Clear any info messages when strategy type changes
                  }}
                  label={t('exitStrategy.strategyType')}
                >
                  <MenuItem value="take_profit">{t('exitStrategy.takeProfit')}</MenuItem>
                  <MenuItem value="stop_loss">{t('exitStrategy.stopLoss')}</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {formData.strategy_type === 'take_profit' && (
              <TakeProfitStrategy
                coinSymbol={coinSymbol}
                currentPrice={currentPrice}
                holdings={holdings}
                formData={formData}
                setFormData={setFormData}
                customValue={customValue}
                setCustomValue={setCustomValue}
                selectedPreset={selectedPreset}
                setSelectedPreset={setSelectedPreset}
                loadingHistorical={loadingHistorical}
                historicalHighs={historicalHighs}
                fetchHistoricalHigh={fetchHistoricalHigh}
                showTargetPriceError={showTargetPriceError}
                updateStep={updateStep}
                addStep={addStep}
                removeStep={removeStep}
                handleSubmit={handleSubmit}
                error={error}
                calculateTriggerPrices={calculateTriggerPrices}
                loadedStrategy={loadedStrategy}
                percentValue={percentValue}
                setPercentValue={setPercentValue}
                rawPercentValue={rawPercentValue}
                setRawPercentValue={setRawPercentValue}
              />
            )}
            {formData.strategy_type === 'stop_loss' && (
              <StopLossStrategy
                coinSymbol={coinSymbol}
                currentPrice={currentPrice}
                holdings={holdings}
                formData={formData}
                setFormData={setFormData}
                customValue={customValue}
                setCustomValue={setCustomValue}
                selectedPreset={selectedPreset}
                setSelectedPreset={setSelectedPreset}
                loadingHistorical={loadingHistorical}
                historicalLows={historicalLows || {}}
                fetchHistoricalLow={fetchHistoricalLow}
                showTargetPriceError={showTargetPriceError}
                updateStep={updateStep}
                addStep={addStep}
                removeStep={removeStep}
                handleSubmit={handleSubmit}
                error={error}
                calculateTriggerPrices={calculateTriggerPrices}
                loadedStrategy={loadedStrategy}
                percentValue={percentValue}
                setPercentValue={setPercentValue}
                rawPercentValue={rawPercentValue}
                setRawPercentValue={setRawPercentValue}
              />
            )}
        </Grid>
      </Grid>


    </Container>
  );
};

export default ExitStrategyPage; 